import { HttpClient } from '@adapters/HttpClient';
import type {
  CourierInvoiceDetail,
  CourierInvoiceListItem,
  CourierInvoicesListResponse,
  CreateCourierInvoiceBody,
  CreateCourierInvoiceResponse,
  ImportCourierInvoiceResponse,
  PaginatorMeta,
  RecentCourierSettlement,
} from '../models/types';

const LIST_PATH = '/courier-invoices';

function parseMeta(m: Record<string, unknown> | undefined, fallbackPerPage: number, page: number): PaginatorMeta {
  const raw = m ?? {};
  return {
    total: Number(raw.total ?? 0),
    per_page: Number(raw.per_page ?? (raw as { perPage?: number }).perPage ?? fallbackPerPage),
    current_page: Number(raw.current_page ?? (raw as { currentPage?: number }).currentPage ?? page),
    last_page: Number(raw.last_page ?? (raw as { lastPage?: number }).lastPage ?? 1),
  };
}

/** Si la API no devuelve `meta`, inferimos paginación para no romper el listado ni el scroll infinito. */
function inferCourierInvoicesListMeta(dataLen: number, page: number, per_page: number): PaginatorMeta {
  const oversizedSinglePayload = dataLen > per_page;
  const last_page = oversizedSinglePayload ? page : dataLen === 0 || dataLen < per_page ? page : page + 1;
  const cumulative = (page - 1) * per_page + dataLen;
  return {
    total: last_page === page ? cumulative : Math.max(cumulative, 1),
    per_page: oversizedSinglePayload ? Math.max(dataLen, per_page) : per_page,
    current_page: page,
    last_page,
  };
}

function mapListItem(raw: Record<string, unknown>): CourierInvoiceListItem {
  const courierRaw = raw.courier as Record<string, unknown> | undefined;
  return {
    id: Number(raw.id),
    courier_id: raw.courier_id !== undefined ? Number(raw.courier_id) : undefined,
    courier:
      courierRaw && courierRaw.id !== undefined
        ? { id: Number(courierRaw.id), name: String(courierRaw.name ?? '') }
        : raw.courier === null
          ? null
          : undefined,
    supplier_reference: raw.supplier_reference != null ? String(raw.supplier_reference) : null,
    invoiced_at: raw.invoiced_at != null ? String(raw.invoiced_at) : null,
    period_start: raw.period_start != null ? String(raw.period_start) : null,
    period_end: raw.period_end != null ? String(raw.period_end) : null,
    currency: raw.currency != null ? String(raw.currency) : null,
    notes: raw.notes != null ? String(raw.notes) : null,
    status: raw.status != null ? String(raw.status) : null,
    settlements_count: raw.settlements_count !== undefined ? Number(raw.settlements_count) : undefined,
  };
}

function mapRecentSettlement(raw: Record<string, unknown>): RecentCourierSettlement {
  return {
    id: Number(raw.id),
    source_filename: raw.source_filename != null ? String(raw.source_filename) : null,
    import_status: raw.import_status != null ? String(raw.import_status) : null,
    imported_at: raw.imported_at != null ? String(raw.imported_at) : null,
    lines_total: raw.lines_total !== undefined ? Number(raw.lines_total) : null,
    lines_matched_count: raw.lines_matched_count !== undefined ? Number(raw.lines_matched_count) : null,
    created_at: raw.created_at != null ? String(raw.created_at) : null,
  };
}

export class CourierInvoicesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async list(page = 1, per_page = 25): Promise<CourierInvoicesListResponse> {
    const raw = await this.httpClient.get<{ data: unknown[]; meta?: Record<string, unknown> }>(
      LIST_PATH,
      { page, per_page },
      {},
      true,
    );
    const rows = Array.isArray(raw.data) ? raw.data : [];
    const data = rows.map((item) => mapListItem(item as Record<string, unknown>));
    const hasMeta = raw.meta != null && typeof raw.meta === 'object' && !Array.isArray(raw.meta);
    const meta = hasMeta
      ? parseMeta(raw.meta as Record<string, unknown>, per_page, page)
      : inferCourierInvoicesListMeta(data.length, page, per_page);
    return { data, meta };
  }

  async detail(id: number): Promise<CourierInvoiceDetail> {
    const rawRoot = (await this.httpClient.get<unknown>(`${LIST_PATH}/${id}`, {}, {}, true)) as Record<string, unknown>;

    const inner = rawRoot.data;
    const raw =
      inner && typeof inner === 'object' && !Array.isArray(inner) ? (inner as Record<string, unknown>) : rawRoot;

    const invoiceCandidate =
      (raw.courier_invoice as Record<string, unknown> | undefined) ??
      (raw.courier_invoices as Record<string, unknown> | undefined) ??
      (typeof raw.id !== 'undefined' ? raw : undefined);

    if (!invoiceCandidate || invoiceCandidate.id === undefined) {
      throw new Error('Respuesta de detalle de factura inválida');
    }

    const invoice = mapListItem(invoiceCandidate);

    const agg = raw.aggregates as Record<string, unknown> | undefined;
    const aggregates = {
      settlements_count: Number(agg?.settlements_count ?? 0),
      courier_charge_lines_total: Number(agg?.courier_charge_lines_total ?? 0),
    };

    const recentRaw = raw.recent_settlements;
    const recent_settlements = Array.isArray(recentRaw)
      ? recentRaw.map((r) => mapRecentSettlement(r as Record<string, unknown>))
      : [];

    return { invoice, aggregates, recent_settlements };
  }

  async create(body: CreateCourierInvoiceBody): Promise<CreateCourierInvoiceResponse> {
    return this.httpClient.post<CreateCourierInvoiceResponse>(LIST_PATH, body, true);
  }

  async importFile(
    invoiceId: number,
    payload: { file: File; period_start?: string; period_end?: string },
  ): Promise<ImportCourierInvoiceResponse> {
    const formData = new FormData();
    formData.append('file', payload.file);
    if (payload.period_start) {
      formData.append('period_start', payload.period_start);
    }
    if (payload.period_end) {
      formData.append('period_end', payload.period_end);
    }

    return this.httpClient.post<ImportCourierInvoiceResponse>(`${LIST_PATH}/${invoiceId}/import`, formData, true, {});
  }
}
